import { useState, useEffect, useCallback } from "react";
import { Box, Button, TextField, Typography, IconButton, Tooltip, CircularProgress, InputAdornment } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import AppDialog from "../General/Dialog/AppDialog";
import ConfirmDialog from "../General/Dialog/ConfirmDialog";
import { useToast } from "../../context/ToastContext";
import {
  getJudicialInterestRates,
  createJudicialInterestRate,
  deleteJudicialInterestRate,
} from "../../services/IndividualLiquidationService";

/**
 * ABM rápido de la tasa "Banco de Córdoba" (TNA anual de pizarra), accesible
 * desde el formulario de liquidación para no tener que ir a Configuración.
 * Bancor no se importa del BCRA: se carga manual con su fecha de vigencia.
 */
export default function BancoCordobaRatesDialog({ open, onClose, onChanged }) {
  const { showToast } = useToast();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(null);
  const [tna, setTna] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getJudicialInterestRates({ rate_type: "banco_cordoba", perPage: 100, page: 1 });
      setRows(res.data?.data ?? []);
    } catch {
      setRows([]);
      showToast("No se pudieron cargar las tasas de Banco de Córdoba.", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (!open) return;
    setDate(null);
    setTna("");
    setNotes("");
    load();
  }, [open, load]);

  const handleAdd = async () => {
    if (!date || !dayjs.isDayjs(date) || !date.isValid()) {
      showToast("Elegí la fecha de vigencia.", "error");
      return;
    }
    if (tna === "" || isNaN(Number(tna)) || Number(tna) <= 0) {
      showToast("Ingresá la TNA anual (mayor a 0).", "error");
      return;
    }
    setSaving(true);
    try {
      await createJudicialInterestRate({
        rate_type: "banco_cordoba",
        date: date.format("YYYY-MM-DD"),
        accumulated_index: null,
        daily_rate: Number(tna),
        notes: notes || null,
      });
      showToast("Tasa de Banco de Córdoba cargada.", "success");
      setDate(null);
      setTna("");
      setNotes("");
      await load();
      onChanged?.();
    } catch (e) {
      showToast(e?.response?.data?.message ?? "No se pudo guardar la tasa.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteJudicialInterestRate(deleteTarget.id);
      setDeleteTarget(null);
      showToast("Tasa eliminada.", "success");
      await load();
      onChanged?.();
    } catch {
      showToast("No se pudo eliminar la tasa.", "error");
    }
  };

  return (
    <>
      <AppDialog
        open={open}
        onClose={onClose}
        title="Tasa Banco de Córdoba"
        subtitle="TNA anual de pizarra · se aplica desde su fecha de vigencia hasta que haya una nueva"
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 0.5 }}>
          {/* Alta */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "flex-start",
              gap: 1.25,
              p: 1.5,
              borderRadius: 2,
              border: "1px solid rgba(0,0,0,0.12)",
            }}
          >
            <DatePicker
              label="Vigente desde"
              value={date}
              onChange={setDate}
              format="DD/MM/YYYY"
              slotProps={{ textField: { size: "small", sx: { width: 160 } } }}
            />
            <TextField
              size="small"
              type="number"
              label="TNA anual"
              value={tna}
              onChange={(e) => setTna(e.target.value)}
              sx={{ width: 130 }}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            />
            <TextField
              size="small"
              label="Nota (opcional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              sx={{ flex: 1, minWidth: 160 }}
            />
            <Button
              variant="contained"
              onClick={handleAdd}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <AddRoundedIcon />}
              sx={{ borderRadius: 2, fontWeight: 800, textTransform: "none", mt: 0.25 }}
            >
              Agregar
            </Button>
          </Box>

          {/* Listado */}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={22} />
            </Box>
          ) : rows.length === 0 ? (
            <Typography sx={{ fontSize: 13, opacity: 0.6, textAlign: "center", py: 2 }}>
              Todavía no hay tasas de Banco de Córdoba cargadas.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {rows.map((r) => (
                <Box
                  key={r.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.25,
                    py: 0.75,
                    borderRadius: 1.5,
                    border: "1px solid rgba(0,0,0,0.08)",
                  }}
                >
                  <Typography sx={{ fontSize: 13, fontWeight: 700, width: 110 }}>
                    {dayjs(r.date).format("DD/MM/YYYY")}
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
                    {r.daily_rate != null
                      ? `${Number(r.daily_rate).toLocaleString("es-AR", { maximumFractionDigits: 2 })}%`
                      : "—"}
                  </Typography>
                  <Typography sx={{ fontSize: 12, opacity: 0.6, flex: 1, minWidth: 0 }} noWrap>
                    {r.notes || ""}
                  </Typography>
                  <Tooltip title="Eliminar">
                    <IconButton
                      size="small"
                      onClick={() => setDeleteTarget(r)}
                      sx={{ opacity: 0.5, "&:hover": { opacity: 1, color: "error.main" } }}
                    >
                      <DeleteOutlineRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </AppDialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Eliminar tasa"
        description={
          deleteTarget
            ? `¿Eliminar la tasa de Banco de Córdoba vigente desde el ${dayjs(deleteTarget.date).format("DD/MM/YYYY")}?`
            : ""
        }
        confirmText="Eliminar"
        confirmColor="error"
      />
    </>
  );
}
